import React, { createContext, useContext, useEffect, useRef, useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import Cache from '@fs/zion-cache'
import { useHistory, useParams } from '@fs/zion-router'
import { useOverlay } from '@fs/zion-ui'
import { useUser } from '@fs/zion-user'

import { CACHE } from '../../helpers'
import { formatTreePersons, getRelatives, getPreferredSpouse } from '../../pages/TreePage/helpers/treeHelpers'
import useImageSettings, { ImageSettingsProvider } from '../useImageSettings/useImageSettings'
import { KeepProvider } from '../useKeep'
import { usePedigree } from '../usePedigree'
// eslint-disable-next-line import/no-cycle -- comment: This is a circular dependency, it will probably be resolved when we impliment frontier's printing
import { usePortraitUrls } from '../usePortraits'
import { ReadyProvider } from '../useReady/useReady'

const TreeContext = createContext()
const visibilityCache = new Cache(CACHE.STORE_VISIBILITY)

function InnerTreeProvider({
  children,
  hasSpousePedigree,
  initialHiddenIds,
  initialShowAvatars,
  initialShowPortraits,
}) {
  const history = useHistory()
  const { cachedBackgroundImage, setBackgroundImage, zoomPan, zoomPanProps } = useImageSettings()
  const { design, personId: focusPersonId, style } = useParams()
  const [buildMode, setBuildMode] = useState(true)
  const [forceBuildMode, setForceBuildMode] = useState(undefined)
  const [fromMemoryBooks, setFromMemoryBooks] = useState(false)
  const [generationsFilled, setGenerationsFilled] = useState(1)
  const [memoryBooksPageNumber, setMemoryBooksPageNumber] = useState(0)
  const [portraits, setPortraits] = useState({})
  const [pedigreeFailed, setPedigreeFailed] = useState(false)
  const [show4thGen, setShow4thGen] = useState(false)
  const [showAvatars, setShowAvatars] = useState(initialShowAvatars ?? false)
  const [showChildren, setShowChildren] = useState(false)
  const [showPortraits, setShowPortraits] = useState(initialShowPortraits ?? false)
  const [showSpouses, setShowSpouses] = useState(false)
  const [showPrintPreview, setShowPrintPreview] = useState(false)
  // const [showSiblings, setShowSiblings] = useState(false) // v3
  const [showSpouse, setShowSpouse] = useState(false)
  const [spouseTreePersons, setSpouseTreePersons] = useState([])
  const [startingPersonOptions, setStartingPersonOptions] = useState([])
  const [treePersons, setTreePersons] = useState([])
  const [treeRowsVisible, setTreeRowsVisible] = useState(1)
  const [treeWithOnlyPOF, setTreeWithOnlyPOF] = useState(false)
  const { personId } = useUser()

  const smallTreeDialogOverlay = useOverlay()
  const [hasSeenSmallTreeDialog, setHasSeenSmallTreeDialog] = useState(false)

  const hasInitializedDataRef = useRef(false)

  const loggedUserIsPof = personId === focusPersonId

  // Get POF data for the tree
  const focusPersonPedigreeData = usePedigree({
    personId: focusPersonId,
    includePreferredSpouse: true,
    initialHiddenIds,
  })
  // Get user data for starting person options
  // Only fetch if different from focus person and focusPersonId is resolved to avoid duplicate hourglass calls
  // (before the URL redirect adds the personId, focusPersonId is undefined which would cause a premature fetch)
  const shouldFetchRelatives = !loggedUserIsPof && Boolean(focusPersonId)
  const relativesPedigreeDataRaw = usePedigree({
    personId: shouldFetchRelatives ? personId : undefined,
    includePreferredSpouse: true,
  })
  // Reuse focusPersonPedigreeData when logged-in user is the person of focus
  const relativesPedigreeData = loggedUserIsPof ? focusPersonPedigreeData : relativesPedigreeDataRaw
  // Get pof data for designs that include spousal family tree
  const preferredSpouseId = hasSpousePedigree
    ? getPreferredSpouse(focusPersonPedigreeData?.pedigree?.pairings)?.id
    : undefined
  const preferredSpousePedigreeData = usePedigree({
    personId: preferredSpouseId,
    isSpousePedigree: true,
    initialHiddenIds,
  })
  const portraitsData = usePortraitUrls(focusPersonPedigreeData?.pedigree)

  const [cardPersonId, setCardPersonId] = useState()
  const personCardOverlay = useOverlay()
  const pedigreeLimitOverlay = useOverlay()
  const [hasSeenPedigreeLimitDialog, setHasSeenPedigreeLimitDialog] = useState(false)

  const refreshPedigree = useCallback(async () => {
    const promises = [focusPersonPedigreeData.refreshPedigreeData()]
    if (shouldFetchRelatives) {
      promises.push(relativesPedigreeData.refreshPedigreeData())
    }
    return Promise.all(promises)
  }, [focusPersonPedigreeData, relativesPedigreeData, shouldFetchRelatives])

  function fetchCachedVisibilityPreferences() {
    // Only load from cache if we don't have initial values from URL
    if (initialShowAvatars === undefined) {
      visibilityCache.getItem(CACHE.SHOW_AVATARS).then((cachedShowAvatars) => {
        setShowAvatars(cachedShowAvatars === false ? cachedShowAvatars : CACHE.SHOW_AVATARS_INITIAL)
      })
    }
    if (initialShowPortraits === undefined) {
      visibilityCache.getItem(CACHE.SHOW_PORTRAITS).then((cachedShowPortraits) => {
        setShowPortraits(cachedShowPortraits === false ? cachedShowPortraits : CACHE.SHOW_PORTRAITS_INITIAL)
      })
    }
  }

  const saveShowAvatars = useCallback(
    (newShowAvatars) => {
      visibilityCache.setItem(CACHE.SHOW_AVATARS, newShowAvatars)
      setShowAvatars(newShowAvatars)
    },
    [setShowAvatars]
  )

  const saveShowPortraits = useCallback(
    (newShowPortraits) => {
      visibilityCache.setItem(CACHE.SHOW_PORTRAITS, newShowPortraits)
      setShowPortraits(newShowPortraits)
    },
    [setShowPortraits]
  )

  useEffect(() => {
    fetchCachedVisibilityPreferences()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (relativesPedigreeData?.pedigree) {
      setStartingPersonOptions(getRelatives(relativesPedigreeData?.pedigree))
    }
  }, [relativesPedigreeData?.pedigree])

  useEffect(() => {
    // Set default settings
    setShow4thGen(focusPersonPedigreeData?.pedigree?.metadata?.generations >= 4)
    const hasSpouse = Boolean(focusPersonPedigreeData?.pedigree?.pairings?.[0]?.spousePedigree?.person?.id)
    setShowSpouse(hasSpouse)
    setShowSpouses(hasSpouse)
    setShowChildren(true)
  }, [focusPersonPedigreeData?.pedigree])

  useEffect(() => {
    if (focusPersonPedigreeData?.lastError) {
      setPedigreeFailed(true)
    } else {
      setPedigreeFailed(false)
    }
  }, [focusPersonPedigreeData?.lastError])

  useEffect(() => {
    if (portraitsData) {
      setPortraits(portraitsData)
    }
  }, [portraitsData])

  useEffect(() => {
    if (style === 'pedigree' && design === 'custom') {
      if (cachedBackgroundImage) {
        setBackgroundImage(cachedBackgroundImage)
      } else {
        history.replace('/')
      }
    }
  }, [cachedBackgroundImage, design, history, setBackgroundImage, style])

  const getFilledGenerations = (persons) => {
    const generations = []
    let index = 0

    // Dynamically generate generations
    for (let generationSize = 1; index < persons.length; generationSize *= 2) {
      const generation = persons.slice(index, index + generationSize)
      generations.push(generation)
      index += generationSize
    }

    // Find the first unfilled generation
    const firstUnfilledGenerationIndex = generations.findIndex(
      (generation) => !generation.every((person) => person?.id)
    )

    // Return the index of the first unfilled generation or the total number of generations
    return firstUnfilledGenerationIndex === -1 ? generations.length : firstUnfilledGenerationIndex
  }

  useEffect(() => {
    const resetZoomPan = () => {
      setTimeout(() => {
        zoomPan.reset()
        zoomPan.set(zoomPanProps)
      }, 300)
    }
    const focusPersonPedigree = focusPersonPedigreeData?.pedigree
    const preferredSpousePedigree = preferredSpousePedigreeData?.pedigree
    if (focusPersonPedigree) {
      const persons = formatTreePersons({ pedigree: focusPersonPedigree })
      setTreePersons(persons)
      if (preferredSpousePedigree) {
        const spousePersons = formatTreePersons({
          pedigree: preferredSpousePedigree,
          isSpousePedigree: true,
        })
        setSpouseTreePersons(spousePersons)
      }

      // The variable 'rowsVisible' includes the row above the oldest person in your tree
      // because we display addPersonButtons on the next row up
      let rowsVisible = 1
      if (persons[3].id || persons[4].id || persons[5].id || persons[6].id) {
        rowsVisible = 4
      } else if (persons[1].id || persons[2].id) {
        // this is a POF with 1 or 2 parents and no grandparents
        rowsVisible = 3
      } else if (persons[0].id) {
        // this is a POF only with no parents added
        rowsVisible = 2
      }
      const numGenerationsFilled = getFilledGenerations(persons)
      setGenerationsFilled(numGenerationsFilled)
      setTreeWithOnlyPOF(persons.slice(1).every((person) => person.id === null))

      if (forceBuildMode !== undefined && forceBuildMode !== buildMode) {
        setBuildMode(forceBuildMode)
        resetZoomPan()
      } else if (!hasInitializedDataRef.current && numGenerationsFilled >= 3) {
        setBuildMode(false)
        resetZoomPan()
      }

      hasInitializedDataRef.current = true
      setTreeRowsVisible(rowsVisible)
    }
  }, [
    buildMode,
    focusPersonPedigreeData?.pedigree,
    zoomPan,
    zoomPanProps,
    forceBuildMode,
    preferredSpousePedigreeData?.pedigree,
  ])

  const memoizedValue = useMemo(
    () => ({
      buildMode,
      cardPersonId,
      focusPersonId,
      fromMemoryBooks,
      generationsFilled,
      hasSeenPedigreeLimitDialog,
      hasSeenSmallTreeDialog,
      loggedUserIsPof,
      memoryBooksPageNumber,
      pedigreeFailed,
      pedigree: focusPersonPedigreeData?.pedigree,
      personCardOverlay,
      pedigreeLimitOverlay,
      portraits,
      refreshPedigree,
      saveShowAvatars,
      saveShowPortraits,
      setBuildMode,
      setForceBuildMode,
      setFromMemoryBooks,
      setCardPersonId,
      setHasSeenPedigreeLimitDialog,
      setHasSeenSmallTreeDialog,
      setMemoryBooksPageNumber,
      setPortraits,
      setShow4thGen,
      setShowChildren,
      setShowPrintPreview,
      setShowSpouse,
      setShowSpouses,
      show4thGen,
      showAvatars,
      showChildren,
      showPortraits,
      showPrintPreview,
      showSpouse,
      showSpouses,
      spouseTreePersons,
      smallTreeDialogOverlay,
      startingPersonOptions,
      treeRowsVisible,
      treePersons,
      treeWithOnlyPOF,
    }),
    [
      buildMode,
      cardPersonId,
      focusPersonId,
      fromMemoryBooks,
      generationsFilled,
      hasSeenPedigreeLimitDialog,
      hasSeenSmallTreeDialog,
      memoryBooksPageNumber,
      loggedUserIsPof,
      pedigreeFailed,
      focusPersonPedigreeData?.pedigree,
      personCardOverlay,
      pedigreeLimitOverlay,
      portraits,
      refreshPedigree,
      saveShowAvatars,
      saveShowPortraits,
      show4thGen,
      showAvatars,
      showChildren,
      showPortraits,
      showPrintPreview,
      showSpouse,
      showSpouses,
      spouseTreePersons,
      smallTreeDialogOverlay,
      startingPersonOptions,
      treeRowsVisible,
      treePersons,
      treeWithOnlyPOF,
    ]
  )

  return (
    <TreeContext.Provider value={memoizedValue}>
      <KeepProvider showAvatars={showAvatars} showPortraits={showPortraits}>
        <ReadyProvider>{children}</ReadyProvider>
      </KeepProvider>
    </TreeContext.Provider>
  )
}

InnerTreeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  hasSpousePedigree: PropTypes.bool,
  initialHiddenIds: PropTypes.arrayOf(PropTypes.string),
  initialShowAvatars: PropTypes.bool,
  initialShowPortraits: PropTypes.bool,
}

export function TreeProvider({
  children,
  hasSpousePedigree,
  initialBackgroundOpacity,
  initialHiddenIds,
  initialShowAvatars,
  initialShowPortraits,
}) {
  return (
    <ImageSettingsProvider initialBackgroundOpacity={initialBackgroundOpacity}>
      <InnerTreeProvider
        hasSpousePedigree={hasSpousePedigree}
        initialHiddenIds={initialHiddenIds}
        initialShowAvatars={initialShowAvatars}
        initialShowPortraits={initialShowPortraits}
      >
        {children}
      </InnerTreeProvider>
    </ImageSettingsProvider>
  )
}

TreeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  hasSpousePedigree: PropTypes.bool,
  initialBackgroundOpacity: PropTypes.number,
  initialHiddenIds: PropTypes.arrayOf(PropTypes.string),
  initialShowAvatars: PropTypes.bool,
  initialShowPortraits: PropTypes.bool,
}

export function useTree() {
  return useContext(TreeContext)
}
